import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { MultilingualCodeSystems, SortDirection } from '@e-origin/shared';

const c = initContract();

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const VALUE_REQUIRED = 'Code value cannot be empty';
const FAMILY_REQUIRED = 'Family cannot be empty';

export const MultilingualCodeTranslationsSchema = z.object({
  en: z.string().optional(),
  fr: z.string().optional(),
  nl: z.string().optional(),
  de: z.string().optional(),
});

export const AdminMultilingualCodeSchema = z.object({
  _id: z.string(),
  family: z.string().min(1, FAMILY_REQUIRED),
  value: z.string().min(1, VALUE_REQUIRED),
  systems: z.array(z.string()),
  translations: MultilingualCodeTranslationsSchema,
});

const SYSTEM_INVALID = `System must be one of: ${Object.values(MultilingualCodeSystems).join(', ')}`;

const MultilingualCodeSystemSchema = z.preprocess(
  (system) => (typeof system === 'string' ? system.trim() : system),
  z.nativeEnum(MultilingualCodeSystems, { errorMap: () => ({ message: SYSTEM_INVALID }) }),
);

export const MultilingualCodeWriteSchema = z.object({
  family: z.string().min(1, FAMILY_REQUIRED),
  value: z.string().min(1, VALUE_REQUIRED),
  systems: z.array(MultilingualCodeSystemSchema),
  translations: MultilingualCodeTranslationsSchema,
});

const paginationSchema = z.object({ page: z.number().optional(), size: z.number().optional() }).optional();
const directionSchema = z.enum([SortDirection.ASC, SortDirection.DESC]).optional();

export const CODE_FAMILY_SORT_FIELDS = ['family'] as const;
export const CODE_SORT_FIELDS = ['value'] as const;

export type CodeFamilySortField = (typeof CODE_FAMILY_SORT_FIELDS)[number];
export type CodeSortField = (typeof CODE_SORT_FIELDS)[number];

export const AdminCodeFamiliesCriteriaSchema = z
  .object({
    filters: z.object({ searchQuery: z.string().optional() }).optional(),
    pagination: paginationSchema,
    sorting: z.object({ field: z.enum(CODE_FAMILY_SORT_FIELDS).optional(), direction: directionSchema }).optional(),
  })
  .optional();

export const AdminCodesCriteriaSchema = z
  .object({
    filters: z.object({ family: z.string().optional(), searchQuery: z.string().optional() }).optional(),
    pagination: paginationSchema,
    sorting: z.object({ field: z.enum(CODE_SORT_FIELDS).optional(), direction: directionSchema }).optional(),
  })
  .optional();

export const AdminCodeFamilySchema = z.object({
  family: z.string(),
  codesCount: z.number(),
});

export type AdminCodeFamily = z.infer<typeof AdminCodeFamilySchema>;
export type AdminMultilingualCode = z.infer<typeof AdminMultilingualCodeSchema>;
export type MultilingualCodeWrite = z.infer<typeof MultilingualCodeWriteSchema>;
export type MultilingualCodeTranslations = z.infer<typeof MultilingualCodeTranslationsSchema>;

export const AdminCodeFamiliesResponseSchema = z.object({
  data: z.object({
    families: z.array(AdminCodeFamilySchema),
    totalItems: z.number(),
  }),
});

export const AdminCodesResponseSchema = z.object({
  data: z.object({
    codes: z.array(AdminMultilingualCodeSchema),
    totalItems: z.number(),
  }),
});

export const AdminCodeResponseSchema = z.object({ data: AdminMultilingualCodeSchema });

export const RenameFamilyResponseSchema = z.object({ data: z.object({ updated: z.number() }) });

export const DeleteFamilyResponseSchema = z.object({ data: z.object({ deleted: z.number() }) });

export const ImportFamilyResponseSchema = z.object({
  data: z.object({ deleted: z.number(), inserted: z.number() }),
});

export type AdminCodeFamiliesResponse = z.infer<typeof AdminCodeFamiliesResponseSchema>;
export type AdminCodesResponse = z.infer<typeof AdminCodesResponseSchema>;
export type AdminCodeResponse = z.infer<typeof AdminCodeResponseSchema>;
export type RenameFamilyResponse = z.infer<typeof RenameFamilyResponseSchema>;
export type DeleteFamilyResponse = z.infer<typeof DeleteFamilyResponseSchema>;
export type ImportFamilyResponse = z.infer<typeof ImportFamilyResponseSchema>;

export const multilingualCodeAdminContract = c.router({
  retrieveFamilies: {
    method: 'POST',
    path: '/external-api/multilingual-codes/admin/families',
    body: z.object({ criteria: AdminCodeFamiliesCriteriaSchema }),
    responses: { 200: AdminCodeFamiliesResponseSchema },
  },
  retrieveCodes: {
    method: 'POST',
    path: '/external-api/multilingual-codes/admin/codes',
    body: z.object({ criteria: AdminCodesCriteriaSchema }),
    responses: { 200: AdminCodesResponseSchema },
  },
  createCode: {
    method: 'POST',
    path: '/external-api/multilingual-codes/admin/codes/add',
    body: MultilingualCodeWriteSchema,
    responses: { 200: AdminCodeResponseSchema },
  },
  updateCode: {
    method: 'POST',
    path: '/external-api/multilingual-codes/admin/codes/edit/:id',
    pathParams: z.object({ id: z.string() }),
    body: MultilingualCodeWriteSchema.partial(),
    responses: { 200: AdminCodeResponseSchema },
  },
  deleteCode: {
    method: 'DELETE',
    path: '/external-api/multilingual-codes/admin/codes/:id',
    body: c.noBody(),
    responses: {
      204: c.noBody(),
    },
  },
  exportFamily: {
    method: 'POST',
    path: '/external-api/multilingual-codes/admin/families/export',
    body: z.object({ family: z.string().min(1, FAMILY_REQUIRED) }),
    responses: {
      200: c.otherResponse({ contentType: XLSX_CONTENT_TYPE, body: z.unknown() }),
    },
  },
  renameFamily: {
    method: 'POST',
    path: '/external-api/multilingual-codes/admin/families/rename',
    body: z.object({
      family: z.string().min(1, FAMILY_REQUIRED),
      newFamily: z.string().min(1, FAMILY_REQUIRED),
    }),
    responses: { 200: RenameFamilyResponseSchema },
  },
  deleteFamily: {
    method: 'POST',
    path: '/external-api/multilingual-codes/admin/families/delete',
    body: z.object({ family: z.string().min(1, FAMILY_REQUIRED) }),
    responses: { 200: DeleteFamilyResponseSchema },
  },
  importFamily: {
    method: 'POST',
    path: '/external-api/multilingual-codes/admin/families/import',
    contentType: 'multipart/form-data',
    body: z.object({
      family: z.string().min(1, FAMILY_REQUIRED),
      mode: z.enum(['create', 'replace']),
      file: z.any(),
    }),
    responses: { 200: ImportFamilyResponseSchema },
  },
});
